import React, { useEffect, useRef, useState, useContext } from 'react';
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from 'react-toastify';
import { Room, Track, RoomEvent } from 'livekit-client';
import canAutoPlay from 'can-autoplay';
import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next';

//import assets
import { ReactComponent as AssembloLogoHor } from "../../assets/img/assemblo-logo-hor.svg";

//import services
import MeetingService from "../../services/meeting.service";
import ChatService from "../../services/chat.service";
import { socket, SocketContext } from "../../services/socket.service";
import VotingService from '../../services/voting.service';

//import components
import ChatComponent from "../../components/chat.component";
import EndedComponent from "../../components/ended.component";
import WaitingComponent from "../../components/waiting.component";
import Spinner from "../../components/ui/spinner.component";

export default function AdminMeetingView() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const mainPlayer = useRef();
    const roomRef = useRef(null);
    const isConnectingRef = useRef(false);

    const [status, setStatus] = useState("not started");
    const [chat, setChat] = useState([]);
    const [message, setMessage] = useState("");
    const [meeting, setMeeting] = useState({});
    const [tracks, setTracks] = useState([]);

    const [voting, setVoting] = useState({});
    const [votingSelect, setVotingSelect] = useState("");
    const [globalVotes, setGlobalVotes] = useState({ "yes": 0, "no": 0, "abstain": 0 });

    const { id } = useParams();
    const socketprovider = useContext(SocketContext);
    const [autoplay, setAutoplay] = useState(false);
    const [connectionData, setConnectionData] = useState({ url: null, token: null, live_token: null });
    const [isValidConnection, setIsValidConnection] = useState(false);
    const [isCheckingConnection, setIsCheckingConnection] = useState(true);

    const [mainPlaceholder, setMainPlaceholder] = useState(true);

    const [open, setOpen] = useState(false)
    
    function sendNewChat() {
        if (message !== "") {
            socketprovider.socket.emit('chat', { id: id, message: message });
            setMessage("");
        }
    }

    // Extract connection data from URL parameters
    useEffect(() => {
        const url = searchParams.get('url');
        const token = searchParams.get('token');
        const live_token = searchParams.get('live_token');
        
        
        if (url && token && live_token) {
            // Decode base64 encoded parameters
            const decodedUrl = atob(url);
            const decodedToken = atob(token);
            const decodedLiveToken = atob(live_token);
            
            setConnectionData({
                url: decodedUrl,
                token: decodedToken,
                live_token: decodedLiveToken
            });
            setIsValidConnection(true);
            setIsCheckingConnection(false);
        } else {
            setIsCheckingConnection(false);
            setIsValidConnection(false);
        }
        
        reloadAutoplay();
    }, [searchParams]);

    //use effect to load meeting
    useEffect(() => {
        MeetingService.getMeeting(id)
            .then(response => {
                setMeeting(response.data);
                document.title = response.data.title;
            })
            .catch(err => {
                toast.error(err.response.data.message);
            });
    }, []);

    useEffect(() => {
        MeetingService.status(id)
            .then(response => {
                setStatus(response.data);
            })
            .catch(err => {
            }
            );

        ChatService.getChats(id)
            .then(response => {
                setChat(response.data);
            }
            );

        VotingService.getVoting(id)
            .then(response => {
                setVoting(response.data);
                VotingService.getAllVotes(response.data.id)
                    .then(response => {
                        setGlobalVotes(response.data);
                    }
                    );
            }
            );
    }, []);



    useEffect(() => {
        if (connectionData.live_token) {
            socketprovider.connect("admin", connectionData.live_token);
            socketprovider.socket.on('meeting', function (data) {
            if (data.id == id && data.status == "start") {
                setStatus("started");
            }
            if (data.id == id && data.status == "end") {
                setStatus("ended")
            }
        });

        socketprovider.socket.on('chat', function (data) {
            if (data.meeting == id) {
                setChat(chat => [...chat, { user: data.user, message: data.message, created_on: data.created_on }]);
            }
        });

        socketprovider.socket.on('question', function (data) {
            if (data.meeting_id == id) {
                setVotingSelect("");
                setVoting(data);
            }
        });

        socketprovider.socket.on('votes', function (data) {
            if (data.meeting_id == id) {
                setGlobalVotes({
                    yes: data.yes,
                    no: data.no,
                    abstain: data.abstain
                })
            }
        });


            return () => {
                socketprovider.socket.off('meeting');
                socketprovider.socket.off('chat');
                socketprovider.socket.off('question');
                socketprovider.socket.off('votes');
                socketprovider.disconnect();
            }
        }
    }, [connectionData.live_token]);

    // Initialize and manage LiveKit room
    useEffect(() => {
        let room = null;

        const initializeRoom = () => {
            if (room) return room;
            
            room = new Room({
                adaptiveStream: true,
                dynacast: true,
                // High quality for admin monitoring - need to see actual stream quality
                videoCaptureDefaults: {
                    resolution: { width: 1280, height: 720, frameRate: 30 },
                },
            });

            // Selective subscription for bandwidth optimization
            const handleTrackPublished = (publication, participant) => {

                // Only subscribe to OBS streams from whip_streamer participant
                const shouldSubscribe = participant.identity === `whip_streamer_${id}`;
                publication.setSubscribed(shouldSubscribe);

                if (shouldSubscribe) {
                } else {
                }
            };

            // Event handlers
            const handleTrackSubscribed = (track, publication, participant) => {
                setTracks(prev => {
                    const exists = prev.some(t => t.track.sid === track.sid);
                    if (exists) return prev;
                    return [...prev, { track, publication, participant }];
                });
            };

            const handleTrackUnsubscribed = (track, _, participant) => {
                setTracks(prev => {
                    const filtered = prev.filter(t => t.track.sid !== track.sid);
                    // Only update if something was actually removed
                    return filtered.length !== prev.length ? filtered : prev;
                });
            };

            const handleParticipantConnected = (participant) => {
            };

            const handleParticipantDisconnected = (participant) => {
                setTracks(prev => {
                    const filtered = prev.filter(t => t.participant.sid !== participant.sid);
                    // Only update if something was actually removed
                    return filtered.length !== prev.length ? filtered : prev;
                });
            };

            const handleDisconnected = (reason) => {
                setTracks([]);
                isConnectingRef.current = false;
            };

            const handleConnectionStateChanged = (state) => {
                if (state === 'connected') {
                    isConnectingRef.current = false;
                } else if (state === 'disconnected') {
                    isConnectingRef.current = false;
                }
            };

            // Attach event listeners
            room.on(RoomEvent.TrackPublished, handleTrackPublished);
            room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
            room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
            room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
            room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
            room.on(RoomEvent.Disconnected, handleDisconnected);
            room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);

            roomRef.current = room;
            return room;
        };

        const connectToRoom = async () => {
            if (!connectionData.url || !connectionData.token || status !== "started" || !autoplay) {
                return;
            }

            if (roomRef.current?.state === 'connected' || isConnectingRef.current) {
                return;
            }

            isConnectingRef.current = true;
            const roomInstance = initializeRoom();

            try {
                await roomInstance.connect(connectionData.url, connectionData.token);
            } catch (error) {
                isConnectingRef.current = false;
                toast.error("Failed to connect to meeting room. Refreshing page...");
                // For admin big screen, reload page after error
                setTimeout(() => {
                    window.location.reload();
                }, 2000); // Give user time to see the error message
            }
        };

        // Attempt connection
        connectToRoom();

        // Cleanup function
        return () => {
            if (roomRef.current) {
                roomRef.current.disconnect();
                roomRef.current = null;
            }
            room = null;
            isConnectingRef.current = false;
        };
    }, [connectionData.url, connectionData.token, status, autoplay]);

    // Track rendering with debug logging - Admin view shows only main player
    useEffect(() => {
        
        if (tracks.length === 0) {
            setMainPlaceholder(true);
            return;
        }

        // Find the main OBS stream from whip_streamer participant
        let obsVideoTrack = null;
        let obsAudioTrack = null;

        tracks.forEach(({ track, publication, participant }) => {
            
            if (participant.identity === `whip_streamer_${id}`) {
                if (track.kind === Track.Kind.Video) {
                    obsVideoTrack = track;
                } else if (track.kind === Track.Kind.Audio) {
                    obsAudioTrack = track;
                }
            }
        });

        // Setup main player with OBS stream
        if (mainPlayer.current && obsVideoTrack) {
            const streamTracks = [obsVideoTrack.mediaStreamTrack];
            
            // Add audio if available
            if (obsAudioTrack) {
                streamTracks.push(obsAudioTrack.mediaStreamTrack);
            }
            
            const stream = new MediaStream(streamTracks);
            mainPlayer.current.srcObject = stream;
            
            if (autoplay) {
                mainPlayer.current.play().catch(e => {
                    if (!open) {
                        setOpen(true);
                    }
                });
            }
            setMainPlaceholder(false);
        } else if (mainPlayer.current) {
            mainPlayer.current.srcObject = null;
            setMainPlaceholder(true);
        }
        
    }, [tracks, autoplay, open]);

    function reloadAutoplay() {
        if (!autoplay) {
            canAutoPlay.video().then(({ result }) => {
                if (result === true) {
                    setAutoplay(true);
                    setOpen(false);

                    // Try to play video if we have tracks
                    if (mainPlayer.current && mainPlayer.current.srcObject) {
                        mainPlayer.current.play().catch(e => {});
                    }
                } else {
                    // Autoplay is blocked, show the modal
                    setOpen(true);
                }
            })
        }
    }

    // Show loading while checking connection
    if (isCheckingConnection || !isValidConnection) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-white text-lg">Loading admin meeting view...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {status == "started" ? (
                <div className="flex h-screen max-h-screen overflow-hidden">
                    {/* Main Video Section */}
                    <div className="flex-1 lg:w-4/5 relative bg-black">
                        <div className="w-full h-full flex items-center justify-center">
                            <video
                                className={`w-full h-full object-contain ${mainPlaceholder === true ? "hidden" : "block"}`}
                                autoPlay
                                playsInline
                                ref={mainPlayer}
                            />
                            {mainPlaceholder === true && (
                                <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                                    <div className="text-center px-4">
                                        <AssembloLogoHor className="h-16 sm:h-20 lg:h-24 mx-auto mb-4" />
                                        <h1 className="text-white text-base sm:text-lg lg:text-xl font-bold mb-2">
                                            {t("meeting.busy.biepboepbap")}
                                        </h1>
                                        <h3 className="text-white text-sm sm:text-base font-semibold mb-6">
                                            {t("meeting.busy.backsoon")}
                                        </h3>
                                        <Spinner />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Chat Sidebar */}
                    <div className="w-full lg:w-1/5 bg-gradient-to-r from-[#392b5f] to-[#321548] flex flex-col min-h-0 overflow-hidden">
                        {/* Header Section */}
                        <div className="flex-shrink-0 p-4">
                            <AssembloLogoHor className="h-8 lg:h-12 mx-auto" />
                        </div>

                        {/* Chat Section */}
                        <div className="flex-1 min-h-0 px-6 pb-4 overflow-hidden">
                            <ChatComponent 
                                chat={chat} 
                                message={message} 
                                setMessage={setMessage} 
                                sendNewChat={sendNewChat}
                                hideInput={true} 
                            />
                        </div>
                    </div>
                </div>
            ) : status == "ended" ? (
                <EndedComponent meeting_id={id} isAdmin={true} />
            ) : (
                <WaitingComponent date={meeting.date} lang="nl" />
            )}

            {/* Voting Overlay - Admin Read-Only View */}
            {voting && voting.live && (
                <div className="fixed inset-x-0 bottom-0 z-30 flex justify-center p-4">
                    <div className="w-full max-w-md bg-white rounded-lg shadow-xl p-6">
                        <div className="mb-4">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                                Live Stemming
                            </h3>
                        </div>

                        {/* Voting Results */}
                        <div className="space-y-3">
                            {/* Yes votes */}
                            <div className="flex items-center space-x-3">
                                <div className="w-16 h-8 bg-lime-400 rounded flex items-center justify-center">
                                    <span className="text-white text-xs font-semibold">
                                        {(() => {
                                            const totalVotes = globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"];
                                            return totalVotes > 0 ? `${Math.round((globalVotes["yes"] / totalVotes) * 100)}%` : '0%';
                                        })()}
                                    </span>
                                </div>
                                <div className="flex-1 bg-gray-200 rounded-full h-3">
                                    <div
                                        className="bg-lime-400 h-3 rounded-full transition-all duration-500"
                                        style={{
                                            width: `${(() => {
                                                const totalVotes = globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"];
                                                return totalVotes > 0 ? (globalVotes["yes"] / totalVotes) * 100 : 0;
                                            })()}%`
                                        }}
                                    />
                                </div>
                                <span className="text-xs text-gray-600 w-12">
                                    {globalVotes["yes"]} {globalVotes["yes"] === 1 ? 'stem' : 'stemmen'}
                                </span>
                            </div>

                            {/* No votes */}
                            <div className="flex items-center space-x-3">
                                <div className="w-16 h-8 bg-red-500 rounded flex items-center justify-center">
                                    <span className="text-white text-xs font-semibold">
                                        {(() => {
                                            const totalVotes = globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"];
                                            return totalVotes > 0 ? `${Math.round((globalVotes["no"] / totalVotes) * 100)}%` : '0%';
                                        })()}
                                    </span>
                                </div>
                                <div className="flex-1 bg-gray-200 rounded-full h-3">
                                    <div
                                        className="bg-red-500 h-3 rounded-full transition-all duration-500"
                                        style={{
                                            width: `${(() => {
                                                const totalVotes = globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"];
                                                return totalVotes > 0 ? (globalVotes["no"] / totalVotes) * 100 : 0;
                                            })()}%`
                                        }}
                                    />
                                </div>
                                <span className="text-xs text-gray-600 w-12">
                                    {globalVotes["no"]} {globalVotes["no"] === 1 ? 'stem' : 'stemmen'}
                                </span>
                            </div>

                            {/* Abstain votes */}
                            <div className="flex items-center space-x-3">
                                <div className="w-16 h-8 bg-slate-600 rounded flex items-center justify-center">
                                    <span className="text-white text-xs font-semibold">
                                        {(() => {
                                            const totalVotes = globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"];
                                            return totalVotes > 0 ? `${Math.round((globalVotes["abstain"] / totalVotes) * 100)}%` : '0%';
                                        })()}
                                    </span>
                                </div>
                                <div className="flex-1 bg-gray-200 rounded-full h-3">
                                    <div
                                        className="bg-slate-600 h-3 rounded-full transition-all duration-500"
                                        style={{
                                            width: `${(() => {
                                                const totalVotes = globalVotes["yes"] + globalVotes["no"] + globalVotes["abstain"];
                                                return totalVotes > 0 ? (globalVotes["abstain"] / totalVotes) * 100 : 0;
                                            })()}%`
                                        }}
                                    />
                                </div>
                                <span className="text-xs text-gray-600 w-12">
                                    {globalVotes["abstain"]} {globalVotes["abstain"] === 1 ? 'stem' : 'stemmen'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!autoplay && connectionData.url && connectionData.token ? (
                <Transition.Root show={open} as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={() => {}}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>

                        <div className="fixed inset-0 z-10 overflow-y-auto">
                            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                >
                                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                        <div>
                                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                                <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                                            </div>
                                            <div className="mt-3 text-center sm:mt-5">
                                                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                                    {t("meeting.busy.ready")}
                                                </Dialog.Title>
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-500">
                                                        {t("meeting.busy.readysub")}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-5 sm:mt-6">
                                            <button
                                                type="button"
                                                className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                                onClick={() => reloadAutoplay()}
                                            >
                                                {t("meeting.busy.readybutton")}
                                            </button>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>
            ) : null}

        </>

    )
}
